/**
 * BLEVE e bola de fogo — modelo de chama sólida esférica.
 *
 * Correlações do CCPS (Guidelines for Chemical Process QRA, 2000; e
 * Guidelines for VCE, PV Burst, BLEVE and Flash Fire Hazards, 2010):
 *
 *   D = 5,8·M^(1/3)                         diâmetro máximo (m, M em kg)
 *   t = 0,45·M^(1/3)   se M < 30.000 kg     duração (s), regime de momento
 *   t = 2,6·M^(1/6)    se M ≥ 30.000 kg     duração (s), regime de empuxo
 *   H = 0,75·D                              altura do centro (m)
 *   E = Frad·M·ΔHc / (π·D²·t)               poder emissivo (Roberts)
 *
 * Radiação recebida num alvo no solo, à distância horizontal x:
 *
 *   L = √(x² + H²),  F = (R/L)²             fator de vista (alvo voltado para a bola, cos θ = 1)
 *   τ = 2,02·(pw·(L − R))^(−0,09)           transmissividade (TNO "Yellow Book")
 *   q = τ·F·E
 *
 * O fator de vista com o alvo voltado para a bola é o caso mais severo; é o
 * usado em análise de consequência quando não se sabe a orientação do alvo.
 *
 * É um modelo de ensino: não considera a forma real da chama, o terreno, a
 * proteção de construções nem os fragmentos.
 */

/** Teto de poder emissivo observado em ensaios de bola de fogo de hidrocarboneto, kW/m². */
export const PODER_EMISSIVO_MAXIMO_KWM2 = 400
/** Massa a partir da qual o CCPS passa a usar a correlação de duração por empuxo. */
export const MASSA_TRANSICAO_REGIME_KG = 30_000

/**
 * Fração da energia de combustão irradiada. O CCPS indica 0,25 para ruptura
 * abaixo da pressão de ajuste da válvula de alívio e 0,4 para ruptura acima
 * dela (o tanque já aquecido no fogo).
 */
export const FRACOES_RADIANTES = {
  abaixoDoAlivio: 0.25,
  tipica: 0.3,
  acimaDoAlivio: 0.4,
} as const

export interface ParametrosBolaDeFogo {
  massaKg: number
  calorCombustaoMJKg: number
  fracaoRadiante: number
}

export interface BolaDeFogo {
  massaKg: number
  diametroM: number
  duracaoS: number
  alturaCentroM: number
  regime: 'momento' | 'empuxo'
  poderEmissivoKWm2: number
  /** O valor de Roberts passou do teto e foi limitado. */
  poderEmissivoLimitado: boolean
}

export interface Ambiente {
  temperaturaC: number
  /** 0..1 */
  umidadeRelativa: number
}

export const AMBIENTE_PADRAO: Ambiente = { temperaturaC: 25, umidadeRelativa: 0.7 }

export function bolaDeFogo({ massaKg, calorCombustaoMJKg, fracaoRadiante }: ParametrosBolaDeFogo): BolaDeFogo {
  if (!(massaKg > 0) || !Number.isFinite(massaKg)) throw new Error('A massa de produto precisa ser positiva.')
  if (!(calorCombustaoMJKg > 0)) throw new Error('O calor de combustão precisa ser positivo.')
  if (!(fracaoRadiante > 0 && fracaoRadiante <= 0.5)) throw new Error('A fração radiante fica entre 0 e 0,5.')

  const raizCubica = Math.cbrt(massaKg)
  const diametroM = 5.8 * raizCubica
  const regime = massaKg < MASSA_TRANSICAO_REGIME_KG ? 'momento' : 'empuxo'
  const duracaoS = regime === 'momento' ? 0.45 * raizCubica : 2.6 * Math.pow(massaKg, 1 / 6)
  const roberts = (fracaoRadiante * massaKg * calorCombustaoMJKg * 1e6) / (Math.PI * diametroM ** 2 * duracaoS) / 1000

  return {
    massaKg,
    diametroM,
    duracaoS,
    alturaCentroM: 0.75 * diametroM,
    regime,
    poderEmissivoKWm2: Math.min(roberts, PODER_EMISSIVO_MAXIMO_KWM2),
    poderEmissivoLimitado: roberts > PODER_EMISSIVO_MAXIMO_KWM2,
  }
}

/** Pressão de saturação do vapor d’água, Pa (Magnus, Alduchov & Eskridge 1996). */
export function pressaoSaturacaoAguaPa(temperaturaC: number): number {
  return 610.94 * Math.exp((17.625 * temperaturaC) / (temperaturaC + 243.04))
}

/** Transmissividade atmosférica para o caminho X (m) com pressão parcial de água pw (Pa). */
export function transmissividade(pressaoParcialAguaPa: number, caminhoM: number): number {
  const produto = pressaoParcialAguaPa * caminhoM
  if (produto <= 0) return 1
  return Math.min(1, 2.02 * Math.pow(produto, -0.09))
}

/** Fluxo térmico recebido no solo à distância horizontal x do ponto abaixo da bola, kW/m². */
export function fluxoTermicoKWm2(bola: BolaDeFogo, distanciaHorizontalM: number, ambiente: Ambiente): number {
  const raio = bola.diametroM / 2
  const L = Math.hypot(distanciaHorizontalM, bola.alturaCentroM)
  if (L <= raio) return bola.poderEmissivoKWm2
  const fatorVista = (raio / L) ** 2
  const pw = ambiente.umidadeRelativa * pressaoSaturacaoAguaPa(ambiente.temperaturaC)
  return transmissividade(pw, L - raio) * fatorVista * bola.poderEmissivoKWm2
}

/**
 * Distância horizontal em que o fluxo cai para `limiarKWm2`. `null` quando nem
 * logo abaixo da bola o fluxo chega ao limiar.
 */
export function raioParaFluxoM(bola: BolaDeFogo, limiarKWm2: number, ambiente: Ambiente): number | null {
  if (fluxoTermicoKWm2(bola, 0, ambiente) < limiarKWm2) return null
  let perto = 0
  let longe = bola.diametroM
  while (fluxoTermicoKWm2(bola, longe, ambiente) > limiarKWm2) longe *= 2
  // o fluxo é monótono decrescente em x: bissecção até centímetros
  for (let i = 0; i < 60 && longe - perto > 0.01; i++) {
    const meio = (perto + longe) / 2
    if (fluxoTermicoKWm2(bola, meio, ambiente) > limiarKWm2) perto = meio
    else longe = meio
  }
  return (perto + longe) / 2
}

/** Dose térmica de Eisenberg, (kW/m²)^(4/3)·s — "TDU". */
export const doseTermicaTDU = (fluxoKWm2: number, duracaoS: number): number => Math.pow(fluxoKWm2, 4 / 3) * duracaoS

export function massaNoTanqueKg(volumeM3: number, enchimento: number, densidadeLiquidoKgM3: number): number {
  if (!(enchimento >= 0 && enchimento <= 1)) throw new Error('O enchimento vai de 0 a 100%.')
  return volumeM3 * enchimento * densidadeLiquidoKgM3
}

export interface EstadoBolaNoTempo {
  diametroM: number
  alturaCentroM: number
  /** 0..1, para o brilho na animação. */
  intensidade: number
}

/**
 * Forma da bola ao longo da duração, para a animação (fração 0..1 da duração).
 * Ilustrativa, não faz parte do cálculo de radiação: cresce como t^(1/3) até o
 * diâmetro máximo no primeiro terço (Hardee et al., 1978), sobe do chão até a
 * altura do centro ao longo da duração e se apaga no último quarto.
 */
export function estadoNoTempo(bola: BolaDeFogo, fracao: number): EstadoBolaNoTempo {
  const f = Math.min(1, Math.max(0, fracao))
  const crescimento = Math.min(1, f / (1 / 3))
  const diametroM = bola.diametroM * Math.cbrt(crescimento)
  const alturaCentroM = diametroM / 2 + (bola.alturaCentroM - bola.diametroM / 2) * f
  const intensidade = f <= 0.75 ? 1 : 1 - (f - 0.75) / 0.25
  return { diametroM, alturaCentroM, intensidade }
}
